/* eslint-disable complexity */
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../helpers/analytics';
import useCheckMobile from '../../helpers/checkMobile';
import { getDisplayReview } from '../../helpers/merchantReviews';
import ThemeContext from '../../providers/ThemeProvider';
import Stars from '../_generic/stars/Stars';

const DealsReview = ({
  deal,
  secondaryDeal,
  mediumDeal,
  xlDeal,
  categoryMainDeal,
  twoByTwo,
  isXS,
  merchandisingModule,
  countColor = false,
}) => {
  const theme = useContext(ThemeContext);
  const isMobile = useCheckMobile();
  const [isClient, setIsClient] = useState(false);
  const firstTracked = useRef(false);
  const componentRef = useRef(null);
  const isNonMainDeal = [
    secondaryDeal,
    mediumDeal,
    xlDeal,
    categoryMainDeal,
    twoByTwo,
    merchandisingModule,
    isMobile,
  ].some(Boolean);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const dealTiles = true;
  const { average = 0, total = 0 } = getDisplayReview({
    deal,
    dealTiles,
  });

  // Since mrResult is always 'mrValB', we only render when we have data
  const shouldRenderComponent = average && total;

  // Tracking useEffect
  useEffect(() => {
    if (!componentRef.current || !isClient) {
      return;
    }

    const threshold = isMobile ? 1 : 0.1;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Track when component is visible and has valid data
          const shouldTrack = typeof average === 'number' && average > 0;

          if (shouldTrack && !firstTracked.current) {
            trackEvent('deal_tile_review_rendered', { reviewRating: average });
            firstTracked.current = true;
            observer.disconnect();
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold,
      },
    );

    observer.observe(componentRef.current);

    return () => observer.disconnect();
  }, [isClient, isMobile, average]);

  if (!isClient || !shouldRenderComponent) return null;

  return (
    <div className="reviews" ref={componentRef}>
      <Stars isNonMainDeal={isNonMainDeal} value={average} />
      <span className="count-container">{` (${total})`}</span>

      <style jsx>{`
        .reviews {
          color: ${isNonMainDeal
            ? theme.colors.infoyellow
            : theme.colors.primary};
          font-size: ${isNonMainDeal ? '11px' : '14px'};
          margin: ${merchandisingModule ? '5px 10px' : '5px auto'};
          ${isXS ? 'margin: 0 0;' : ''}
          ${merchandisingModule ? 'margin-top: -10px;' : ''}
          ${categoryMainDeal ? 'margin-top: 15px; margin-bottom: -10px;' : ''}
          ${mediumDeal ? 'margin-left: 10px;' : ''}
          justify-content: ${secondaryDeal || merchandisingModule
            ? 'flex-end'
            : 'center'};
          display: flex;
        }
        .count-container {
          color: black;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          .reviews {
            color: ${theme.colors.infoyellow};
            margin-left: 0px;
            font-size: 14px;
            justify-content: flex-start;
          }
          .count-container {
            color: ${countColor ? 'black' : 'white'};
            margin-left: 4px;
          }
        }
      `}</style>
    </div>
  );
};

DealsReview.propTypes = {
  categoryMainDeal: PropTypes.bool,
  deal: PropTypes.shape({
    merchantReviewRating: PropTypes.shape({
      average: PropTypes.number,
      total: PropTypes.number,
    }),
    reviewSummary: PropTypes.shape({
      average: PropTypes.number,
      total: PropTypes.number,
    }),
  }),
  isXS: PropTypes.bool,
  mediumDeal: PropTypes.bool,
  merchandisingModule: PropTypes.bool,
  secondaryDeal: PropTypes.bool,
  twoByTwo: PropTypes.bool,
  xlDeal: PropTypes.bool,
};

export default DealsReview;
